/**
 * playerClassification — THE canonical batter/pitcher section classifier.
 *
 * DD-TWP-0708-FIX (Bible §4.5.9.1). ONE source of truth (Rule 12) for the
 * question "does this player belong in the HITTING section or the PITCHING
 * section?" and for the raw predicate "is this position a pitcher position?".
 *
 * THE RULE (owner-ratified 2026-07-08):
 *   - Active-slot players classify by the SLOT they occupy: a player in a
 *     `P_*` slot is a pitcher there; a player in any hitting slot is a hitter
 *     there. Their primary MLB position string is NOT consulted. This is what
 *     fixes two-way players — Shohei Ohtani (primary `TWP`) at `P_6` is a
 *     pitcher, at `UTIL_0` is a hitter.
 *   - Reserve players (bench / DL / minors / free-agent — no active slot to
 *     key on) classify by primary MLB position. `TWP` has no single natural
 *     side, so per the bench rule it defaults to the HITTING section and is
 *     surfaced with a two-way badge (see `isTwoWayPlayer`).
 *
 * PRIOR DEFECT: every roster/stats site hardcoded
 * `['SP', 'RP', 'P'].includes(primaryPosition)`, which OMITS the canonical
 * `TWP` position (it is a first-class position in core/position_utils.py).
 * A `TWP` in a pitching slot was therefore classified as a hitter, failed to
 * match any hitting slot, and vanished from the roster display — the slot
 * rendered "Empty". See TWP_ELIGIBILITY_INVESTIGATION_20260708.md.
 *
 * ENFORCEMENT: the `['SP','RP','P']` classification literal is BANNED
 * everywhere except this file, pinned by
 * src/__tests__/playerClassification.architecture.test.js (same source-
 * inspection pattern as the cash-writer and league-prop tests). New code
 * routes through `isPitcherPosition` / `classifyPlayerSection` — never a
 * hand-rolled position list.
 */
import { slotTypeFromPosition } from './positionEligibility';

// Canonical pitcher POSITION vocabulary — the MLB primary-position codes that
// pitch. Mirrors the pitcher keys of core/position_utils.py POSITION_ELIGIBILITY
// (`SP`, `RP`, `P`). `TWP` is deliberately NOT here: a two-way player is only a
// pitcher for classification when the SLOT he occupies is a pitching slot.
// A closer is canonically `RP` — `CP` is NOT a canonical position.
export const PITCHER_POSITIONS = Object.freeze(['SP', 'RP', 'P']);

// RAW / source-tolerant pitcher vocabulary — for surfaces that consume
// NON-normalized position codes straight from a pricing or draft feed, where a
// closer may still arrive as the non-canonical `CP`. Use `isRawPitcherPosition`
// ONLY in those raw contexts (pricing/preview, draft available-pool); canonical
// player objects (rosters, live scoring) MUST use `isPitcherPosition` /
// slot-based `classifyPlayerSection`, which never see `CP`.
export const RAW_PITCHER_POSITIONS = Object.freeze(['SP', 'RP', 'CP', 'P']);

// The pitching roster SLOT type. Fantasy roster slots collapse SP/RP into a
// single `P` slot, so `P` is the only pitching slot type.
const PITCHER_SLOT_TYPES = Object.freeze(['P']);

/**
 * Is a CANONICAL MLB / primary position string a pitcher position?
 * @param {string} position e.g. 'SP', 'RP', 'P', 'OF', 'TWP'
 * @returns {boolean}
 */
export const isPitcherPosition = (position) =>
  PITCHER_POSITIONS.includes(position);

/**
 * Is a RAW (non-normalized) position code a pitcher — tolerating the
 * non-canonical closer code `CP`. Use ONLY on pricing/draft feeds that carry
 * unnormalized positions. Canonical surfaces use `isPitcherPosition`.
 * @param {string} position
 * @returns {boolean}
 */
export const isRawPitcherPosition = (position) =>
  RAW_PITCHER_POSITIONS.includes(position);

/**
 * Is a roster slot id a pitching slot? Accepts a full slot id ('P_6') or a
 * bare slot type ('P'); non-pitching / bench / null → false.
 * @param {string} slotId
 * @returns {boolean}
 */
export const isPitcherSlot = (slotId) =>
  PITCHER_SLOT_TYPES.includes(slotTypeFromPosition(slotId));

/**
 * True for a two-way player (primary position `TWP`). Used to render the
 * two-way display badge on the reserve/bench view.
 * @param {Object} player canonical player object
 * @returns {boolean}
 */
export const isTwoWayPlayer = (player) =>
  player?.info?.position === 'TWP';

/**
 * Canonical roster section for a player: 'pitching' | 'hitting'.
 *
 * Active-slot players → by the slot they occupy (`P_*` → pitching).
 * Reserve players (no active slot) → by primary position; `TWP` → 'hitting'.
 *
 * @param {Object} player canonical player object (`info.position`,
 *                        `league_context.roster.{status,position}`)
 * @returns {'pitching'|'hitting'}
 */
export const classifyPlayerSection = (player) => {
  const status = player?.league_context?.roster?.status;
  const slotId = player?.league_context?.roster?.position;
  if (status === 'active' && slotId) {
    return isPitcherSlot(slotId) ? 'pitching' : 'hitting';
  }
  // Reserve: no active slot to key on — fall back to primary MLB position.
  // TWP is not a pitcher position, so it defaults to the hitting section.
  return isPitcherPosition(player?.info?.position) ? 'pitching' : 'hitting';
};

/** Convenience: does this player belong in the pitching section? */
export const isPitcherSection = (player) =>
  classifyPlayerSection(player) === 'pitching';

/** Convenience: does this player belong in the hitting section? */
export const isHitterSection = (player) =>
  classifyPlayerSection(player) === 'hitting';
